/**
 * The exit questionnaires of one opt-in feature, and which of them is actually asked.
 *
 * It reads the **surveys** module, and only reads it: the link is a column on the survey
 * (`Survey.requiresOptInFeatureId`) and nothing on the feature points back (R23). That direction is
 * why this is a hook here rather than a field on the feature payload — the opt-in screens have to
 * go and look.
 *
 * Two screens need the same answer (the cadastro's ④ block and the adoption panel), so the query
 * and the "which one is served" rule live in one place. React Query dedupes the request by key, so
 * both mounting at once still costs one call.
 *
 * ## Why it is still a list, now that a feature has only one (R45)
 *
 * The BFF refuses a second live `ON_OPT_OUT` survey on the same feature and a partial unique index
 * makes it impossible, so in practice `surveys` holds zero or one. It stays a list for two reasons,
 * and neither is hypothetical:
 *
 * - **rows written before that rule.** The panel reads whatever the tenant's database holds, and a
 *   database written before the index existed can still have two. Collapsing to a single survey
 *   here would hide one of them from the only screen that could show it;
 * - **`served` is not "the one that exists".** One questionnaire attached and none servable is the
 *   normal R35 case — a draft, switched off, or outside its window — and that is `surveys.length
 *   === 1` with `served: null`. A hook returning one survey could not tell those apart, and the
 *   whole point of the ④ block is saying "ninguém está sendo perguntado" out loud.
 *
 * @module Pages/BUs/OptInFeatures
 */

import { useSurveys } from '@/Pages/BUs/Surveys/Hooks/useSurveys';
import type { Survey } from '@/Pages/BUs/Surveys/Types/Surveys.types';
import { pickServedExitSurvey } from '@/Pages/BUs/Surveys/Utils/surveyFormat';

export interface UseExitSurveys {
  /**
   * Every live `ON_OPT_OUT` survey naming this feature, whatever state it is in.
   *
   * Zero or one, since R45 — more than that means rows older than the rule.
   */
  surveys: Survey[];
  /**
   * The one a person leaving would get, or `null`.
   *
   * `null` with a non-empty `surveys` is the silent failure of R35: questionnaires exist, none is
   * delivered, and no error is raised anywhere.
   */
  served: Survey | null;
  isLoading: boolean;
}

export const useExitSurveys = (featureId?: string): UseExitSurveys => {
  const { data, isLoading } = useSurveys(
    { requiresOptInFeatureId: featureId, trigger: 'ON_OPT_OUT' },
    Boolean(featureId),
  );

  const surveys = featureId ? (data?.data ?? []) : [];

  return {
    surveys,
    served: pickServedExitSurvey(surveys),
    isLoading: Boolean(featureId) && isLoading,
  };
};
